import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';

import { Buttons } from 'shared';
import { useTranslation } from 'hooks/useTranslation';
import { useValidation } from 'hooks';
import { getQueryParamFromSearch } from 'utils';

import { ChangePasswordInput } from 'features/auth/ts';
import { BasePasswordInputField } from 'features/auth/components/BasePasswordInputField';
import { ReturnToLoginLink } from 'features/auth/components/ReturnToLoginLink';

import { useStyles } from './styles';

const INITIAL_FORM_STATE: ChangePasswordInput = {
  newPassword: '',
  confirmPassword: '',
};

type ChangePasswordFormProps = {
  isLoading: boolean;
  onFormSubmit: (token: string, password: string, isFirstLogin?: boolean) => void;
  isFirstLogin?: boolean;
  buttonTitle?: string;
};

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  isLoading,
  onFormSubmit,
  buttonTitle,
  isFirstLogin = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('auth');
  const { required, password, isEqual } = useValidation();

  const { search } = useLocation();
  const token = useMemo(() => getQueryParamFromSearch(search, 'token'), [search]);

  const form = useForm<ChangePasswordInput>({
    mode: 'onSubmit',
    defaultValues: INITIAL_FORM_STATE,
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting },
  } = form;

  const onSubmit = ({ newPassword }: ChangePasswordInput) => {
    reset({ newPassword: '', confirmPassword: '' });
    token && onFormSubmit(token, newPassword, isFirstLogin);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.passwordFormWrapper}>
        <BasePasswordInputField
          required
          name="newPassword"
          control={control}
          fullWidth
          placeholder={t('newPassword')}
          label={t('newPassword')}
          rules={{
            validate: {
              ...required,
              ...password,
            },
          }}
        />
        <Box paddingY={16}>
          <BasePasswordInputField
            required
            name="confirmPassword"
            control={control}
            fullWidth
            placeholder={t('confirmNewPassword')}
            label={t('confirmNewPassword')}
            rules={{
              validate: {
                ...required,
                equal: value => isEqual(getValues().newPassword)(value),
              },
            }}
          />
        </Box>

        <Grid>
          <Buttons.Auth
            fullWidth
            disabled={isSubmitting}
            title={buttonTitle ?? t('confirm')}
            variant="outlined"
            color="primary"
            loading={isLoading}
          />
        </Grid>
      </form>

      {!isFirstLogin && <ReturnToLoginLink />}
    </FormProvider>
  );
};
